import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { urlRegex } from '@core/constants/global-constants';
import { ILatestNews, ITag } from '@core/interfaces/ilatest-news';
import { LatestNewsService } from '@core/services/latest-news.service';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';

@Component({
    selector: 'app-latest-news-form',
    templateUrl: './latest-news-form.component.html',
    styleUrls: ['./latest-news-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class LatestNewsFormComponent {
  @Input() uniqueTagNames: string[] = [];
  @Input() uniqueTagIds: number[] = [];
  @Input() isCollapsed = false;
  @Input() latestNewsData: any = [];
  @Output() refreshData: EventEmitter<any> = new EventEmitter<any>();

  availableTags: ITag[] = [];
  isAddingNewTags: boolean = false;
  selectedTags: string[] = [];
  selectedTagIds: number[] = [];
  addNewTags: boolean = false;
  isEdit = false;
  faCalendar = faCalendar;
  public selectedFile: any;
  public formData!: FormData;
  newTagControl = new FormControl('');
  newTag: string = '';
  uncheckedTagIds: number[] = [];
  pageContent!: any | null;
  imageFile!: File;
  isProcessing = false;
  latestNewsItemForm!: FormGroup | any;
  tagSelections: any[] = [];
  latestNews: ILatestNews[] = [];

  date!: { year: number; month: number };

  constructor(
    private fb: FormBuilder,
    private latestNewsItem: LatestNewsService,
    private toastr: ToastrService
  ) {
    this.initForm();
  }

  get title() {
    return this.latestNewsItemForm.get('title');
  }
  get description() {
    return this.latestNewsItemForm.get('description');
  }
  get publishDate() {
    return this.latestNewsItemForm.get('publishDate');
  }
  get hrefLink() {
    return this.latestNewsItemForm.get('hrefLink');
  }
  get hrefText() {
    return this.latestNewsItemForm.get('hrefText');
  }
  get imageUrl() {
    return this.latestNewsItemForm.get('imageUrl');
  }
  get imageFileName() {
    return this.latestNewsItemForm.get('imageFileName');
  }

  ngOnInit() {
    this.getTags();
    this.getLatestNewsItem();
    this.latestNewsItemForm.get('tagSelections').setValue([]);
    this.latestNewsData = this.latestNewsItemForm.value;
  }

  ngOnChanges(): void {
    if (this.latestNewsItemForm && this.latestNewsData) {
      const publishDateValue = this.latestNewsData?.publishDate || new Date();
      let publishDate = new Date(publishDateValue);

      this.latestNewsItemForm.patchValue({
        title: this.latestNewsData?.title || '',
        description: this.latestNewsData?.description || '',
        publishDate: this.latestNewsData?.publishDate
          ? new NgbDate(
            publishDate.getFullYear(),
            publishDate.getMonth() + 1,
            publishDate.getDate()
          )
          : null,
        hrefText: this.latestNewsData?.hrefText || '',
        hrefLink: this.latestNewsData?.hrefLink || '',
        imageUrl: this.latestNewsData?.imageUrl || '',
        imageFileName: this.latestNewsData?.imageFileName || '',
        tagSelections: this.latestNewsData?.tagSelections || [],
      });
    }
  }

  getTags(): void {
    this.latestNewsItem.getTags()
      .pipe(take(1))
      .subscribe((res: ITag[]) => {
        this.availableTags = res;
      });
  }

  getLatestNewsItem(): void {
    this.latestNewsItem.getAll()
      .pipe(take(1))
      .subscribe((res: ILatestNews[]) => {
        this.latestNews = res;
      });
  }

  initForm() {
    this.latestNewsItemForm = this.fb.group({
      title: [null, [Validators.required, Validators.maxLength(100)]],
      description: [null],
      tagSelections: [[]],
      publishDate: [null, [Validators.required]],
      hrefText: [null],
      hrefLink: [null, Validators.pattern(urlRegex)],
      imageUrl: [null, Validators.pattern(urlRegex)],
      imageFileName: [{ value: null, disabled: true }, Validators.required],
    });
    this.latestNewsItemForm.patchValue({
      tagSelections: []
    });
  }

  public fileChange(event: any) {
    if (!this.latestNewsData) this.latestNewsData = {};
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const chosenFile: File = fileList[0];
      this.selectedFile = chosenFile;
      this.latestNewsItemForm.get('imageFileName').markAsDirty();
      this.latestNewsItemForm.patchValue({
        imageFileName: chosenFile.name,
      });
      this.latestNewsData.imageFileName = chosenFile.name;
    }
  }

  public prepareData(): void {
    this.formData = new FormData();

    if (this.selectedFile) {
      this.formData.append(
        'latestNewsItemImage',
        this.selectedFile,
        this.selectedFile.name
      );
    }

    const publishDateValue: NgbDate = this.latestNewsItemForm.get('publishDate').value;

    const formattedPublishDate: Date | null = publishDateValue
      ? new Date(publishDateValue.year, publishDateValue.month - 1, publishDateValue.day)
      : null;

    
    const latestNewsData: ILatestNews = {
      id: this.latestNewsData.id,
      title: this.title.value,
      description: this.description.value,
      publishDate: formattedPublishDate,
      hrefText: this.hrefText.value,
      hrefLink: this.hrefLink.value,
      imageUrl: this.imageUrl.value,
      imageFileName: this.imageFileName.value,
      tagSelections: this.latestNewsData.tagSelections,
    };

    this.formData.append('news', JSON.stringify(latestNewsData));
  }

  handleCheckboxClick(tagId: number): void {
    if (tagId === null) {
      return;
    }
    
    if (!this.isSelected(tagId))
      this.latestNewsData.tagSelections.push({
        tagId: tagId,
        tagName: this.availableTags.find(tag => tag.id === tagId)!.tagName
      });
    else {
      const indexToSplice: number = this.latestNewsData.tagSelections.findIndex((tag: { tagId: number; }) => tag.tagId === tagId);
      this.latestNewsData.tagSelections.splice(indexToSplice, 1);
    }
  }
    
  isSelected(tagId?: number): boolean {
    if (!tagId || !this.latestNewsData || !this.latestNewsData.tagSelections) {
      return false;
    }
    return this.latestNewsData.tagSelections.some(
      (tag: { tagId: number }) => tag.tagId === tagId
    );
  }

  saveForm(): void {
    if (this.latestNewsItemForm.invalid) {
      this.toastr.error('Please fix your form errors!');
      return;
    }

    this.prepareData();
    this.isProcessing = true;
    this.latestNewsItem
      .saveLatestNews(this.formData, 'Latest-News-Item successfully saved!')
      .pipe(take(1))
      .subscribe((data: any) => {
        this.isProcessing = false;
        this.refreshData.emit(true);
      });
  }

  resetForm(): void {
    this.latestNewsItemForm.reset();
    this.selectedFile = null;
    this.formData = new FormData();
  }
}
